import { IonCheckbox, IonIcon, IonItem, IonLabel } from "@ionic/react";
import React from "react";
import {
  chevronDownOutline as downIcon,
  chevronUpOutline as upIcon,
  checkmark,
} from "ionicons/icons";
import clsx from "clsx";

import peopleIcon from "../../assets/icons/people.svg";
import triangleIcon from "../../assets/icons/triangle_down.svg";
import { IClass } from "../../state";
import ToolTip from "../../atoms/ToolTip";

import styles from "./ClassSelectForm.module.scss";

export interface ClassSelectFormProps {
  isOpen: boolean;
  label?: string;
  classes: IClass[];
  multiple?: boolean;
  selected_class_ids: number[];
  disabled_class_ids?: number[];
  disabledReasonByClassId?: { [klassId: number]: string };
  groupByGrade?: boolean;
  singleSelect?: boolean;
  className?: string;
  whiteMode?: boolean;
  onClick: () => void;
  update: (args: { [key: string]: any }) => void;
  disabled?: boolean;
  isSecond?: boolean;
}

interface IClassGroup {
  label: string;
  classes: IClass[];
}

const ClassSelectForm = (props: ClassSelectFormProps) => {
  const classGroups: IClassGroup[] | null = React.useMemo(() => {
    if (!props.groupByGrade) {
      return null;
    }
    const grades = props.classes
      .filter(cls => !cls.is_sub && cls.grade != null)
      .map(cls => cls.grade)
      .filter((grade, index, self) => self.indexOf(grade) === index)
      .sort((a, b) => a - b);
    const groups: IClassGroup[] = grades.map(grade => ({
      label: `${grade}年`,
      classes: props.classes.filter(cls => !cls.is_sub && cls.grade === grade),
    }));
    const others = props.classes.filter(cls => cls.is_sub || cls.grade == null);
    if (others.length > 0) {
      groups.push({ label: "グループ", classes: others });
    }
    return groups;
  }, [props.groupByGrade, props.classes]);

  const toggleKlass = (klassId: number) => {
    // 教科BOX編集時は1BOX=1クラスのため単一選択
    if (props.singleSelect) {
      props.update({ selected_class_ids: [klassId] });
      return;
    }
    if (props.selected_class_ids.includes(klassId)) {
      props.update({
        selected_class_ids: props.selected_class_ids.filter(
          id => id !== klassId,
        ),
      });
    } else {
      props.update({
        selected_class_ids: [...props.selected_class_ids, klassId],
      });
    }
  };

  const renderGradeKlassItem = (cls: IClass, isOtherGroup: boolean) => {
    const isClassDisabled = props.disabled_class_ids?.includes(cls.id) ?? false;
    const disabledReason = isClassDisabled
      ? props.disabledReasonByClassId?.[cls.id]
      : undefined;
    const content = (
      <>
        <IonCheckbox
          checked={props.selected_class_ids.includes(cls.id)}
          className={styles.gradeCheckbox}
        />
        <span className={styles.gradeKlassName}>
          {isOtherGroup ? cls.full_name : cls.name}
        </span>
      </>
    );
    return (
      <li
        key={cls.id}
        className={clsx(
          styles.gradeKlassItem,
          isClassDisabled && styles.disabledItem,
        )}
        onClick={() => {
          if (isClassDisabled) {
            return;
          }
          toggleKlass(cls.id);
        }}
      >
        {disabledReason ? (
          <ToolTip content={disabledReason} placement="bottomLeft" multiline>
            {content}
          </ToolTip>
        ) : (
          content
        )}
      </li>
    );
  };

  const displayNames =
    props.selected_class_ids.length === 0
      ? "未選択"
      : props.selected_class_ids.length >= 2
      ? `${
          props.classes.filter(c => {
            return c.id === props.selected_class_ids[0];
          })[0]?.full_name
        } ...他${props.selected_class_ids.length - 1}`
      : props.classes.filter(c => {
          return c.id === props.selected_class_ids[0];
        })[0]?.full_name;

  return (
    <>
      <IonItem
        className={clsx(
          styles.classItemToggle,
          props.disabled && styles.disabled,
          props.isSecond && styles.second,
          props.className || "",
          props.whiteMode && styles.whiteMode,
        )}
        onClick={props.onClick}
      >
        {props.label && (
          <IonIcon
            icon={peopleIcon}
            color="light"
            slot="start"
            className={styles.airplaneIcon}
          />
        )}
        <IonLabel color="light" className={styles.title}>
          {props.label ?? "共有先"}
        </IonLabel>
        <div className={styles.selectedNames}>{displayNames}</div>

        {props.whiteMode ? (
          <IonIcon
            icon={triangleIcon}
            slot="end"
            className={clsx(
              styles.triangleIcon,
              props.isOpen && styles.triangleIconOpen,
            )}
          />
        ) : props.isOpen ? (
          <IonIcon icon={upIcon} slot="end" className={styles.arrowIcon} />
        ) : (
          <IonIcon icon={downIcon} slot="end" className={styles.arrowIcon} />
        )}
      </IonItem>
      <div
        className={
          props.isOpen
            ? classGroups
              ? styles.gradeArea
              : styles.classArea
            : styles.close
        }
      >
        {classGroups ? (
          <ul className={styles.gradeSelector}>
            {classGroups.map(group => (
              <li key={group.label} className={styles.gradeRow}>
                <span className={styles.gradeLabel}>{group.label}</span>
                <ul className={styles.gradeKlassList}>
                  {group.classes.map(cls =>
                    renderGradeKlassItem(cls, group.label === "グループ"),
                  )}
                </ul>
              </li>
            ))}
          </ul>
        ) : (
          <ul className={styles.classSelector}>
            {props.classes.map(cls => {
              const isClassDisabled =
                props.disabled_class_ids?.includes(cls.id) ?? false;
              const disabledReason = isClassDisabled
                ? props.disabledReasonByClassId?.[cls.id]
                : undefined;
              return (
                <li
                  key={cls.id}
                  className={clsx(
                    styles.classItem,
                    isClassDisabled && styles.disabledItem,
                  )}
                  onClick={() => {
                    if (isClassDisabled) {
                      return;
                    }
                    if (props.selected_class_ids.includes(cls.id)) {
                      if (props.multiple) {
                        const filteredClassIDs =
                          props.selected_class_ids.filter(id => id !== cls.id);
                        props.update({
                          selected_class_ids: filteredClassIDs,
                        });
                      } else {
                        props.update({
                          selectedClassID: 0,
                        });
                      }
                    } else {
                      if (props.multiple) {
                        props.update({
                          selected_class_ids: [
                            ...props.selected_class_ids,
                            cls.id,
                          ],
                        });
                      } else {
                        props.update({
                          selectedClassID: cls.id,
                        });
                      }
                    }
                  }}
                >
                  <div className={styles.clsName}>
                    {cls.full_name}
                    {disabledReason && (
                      <span className={styles.disabledReason}>
                        {disabledReason}
                      </span>
                    )}
                    {props.selected_class_ids.includes(cls.id) && (
                      <IonIcon
                        slot="end"
                        icon={checkmark}
                        className={styles.checkIcon}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
};

export default ClassSelectForm;
